import {
  Alert,
  Flex,
  Heading,
  mediaMixin,
  Page,
  PageBreadcrumb,
  PageContent,
  PageTitle,
  spaceMixin,
  Spacer,
  Text,
  TextButton,
  token,
} from '@zillow/constellation';
import { SpinnerContainer } from '../SpinnerContainer';
import { SubmitRequestForm } from './SubmitRequestForm';
import { useEffect, useMemo, useState } from 'react';
import type { FetchGetHelpArticlesArgs, GetHelpArticlesResponse } from './actions';
import {
  fetchGetGuestHelpTopics,
  fetchGetHelpArticles,
  fetchGetHelpArticlesForLoggedInUsers,
  fetchGetHelpTopics,
} from './actions';
import { HelpArticlesContent } from './HelpArticles';
import type {
  GetHelpTopicListResponseV2Dto,
  HelpCategoryDto,
  HelpTopicV2Dto,
  RentalManagerAuthCheckUserInfoDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import { LoadingConstants, ModalWrapper, useRentalManagerUser } from '../..';
import styled, { css } from 'styled-components';
import { SubmitRequestModalPageFooter } from '.';
import { useLocation } from 'react-router-dom';
import { emitOpenSupportWidgetEvent, emitSubmitWidgetSupportRequestEvent } from './ClickstreamEvents';

export const StyledPage = styled(Page)`
  ${mediaMixin(
    'xl',
    css`
      min-height: 900px;
    `,
  )};
`;

export const StyledSpacer = styled(Spacer)`
  ${mediaMixin(
    'md_lte',
    css`
      border-top: 1px solid ${token('colors.gray300')};
      border-bottom: 1px solid ${token('colors.gray300')};
      position: fixed;
      width: 100%;
      bottom: 0;
      margin-left: -${spaceMixin('sm')};
      margin-bottom: 0;
      padding: ${spaceMixin('sm')};
      background: ${token('colors.white')};
      z-index: ${token('zIndices.modal')};
    `,
  )}
`;

const StyledFlex = styled(Flex)`
  @media (max-width: 1025px) {
    flex-direction: column;
  }
`;

export type FormStateType = {
  category?: HelpCategoryDto;
  topic?: HelpTopicV2Dto;
  typedAddress: string;
  unit: string;
  description: string;
};

interface SubmitRequestModalPageProps {
  setCanSubmit: (val: boolean) => void;
  submitClicked: boolean;
  setSubmitClicked: (val: boolean) => void;
  setCategorySelected?: (val: boolean) => void;
  zrmwUser?: RentalManagerAuthCheckUserInfoDto;
  queryObject?: {
    categoryKey: string | null;
    topicKey: string | null;
    listingAddress: string | null;
    unit: string | null;
    listingAlias: string | null;
  };
  zrmSignInPage?: string;
  renderFooter?: () => JSX.Element;
}

/* Re-usable component that shows the submit request form and help articles side-by-side */
export const SubmitRequestPageComponent = ({
  setCanSubmit,
  submitClicked,
  setSubmitClicked,
  setCategorySelected,
  zrmwUser,
  queryObject,
  zrmSignInPage,
  renderFooter,
}: SubmitRequestModalPageProps) => {
  const [errorAlert, setErrorAlert] = useState('');
  const [requestState, setRequestState] = useState<LoadingConstants>(LoadingConstants.INITIAL);

  const [formState, setFormState] = useState<FormStateType>({
    category: undefined,
    topic: undefined,
    typedAddress: queryObject?.listingAddress || '',
    unit: queryObject?.unit || '',
    description: '',
  });

  const [helpTopics, setHelpTopics] = useState<GetHelpTopicListResponseV2Dto>();
  const [helpArticles, setHelpArticles] = useState<GetHelpArticlesResponse>();

  const nonZrmwUser = useRentalManagerUser().user;
  const user = zrmwUser || nonZrmwUser;
  const { loggedIn } = user;
  const isUsingWidget = !renderFooter;

  const helpTopicsNotAvailable = helpTopics === undefined;
  const helpArticlesNotAvailable = helpArticles === undefined;

  useEffect(() => {
    if (loggedIn)
      fetchGetHelpTopics(setHelpTopics, () => setErrorAlert('An error has occurred. Please try again later.'));
    else fetchGetGuestHelpTopics(setHelpTopics, () => setErrorAlert('An error has occurred. Please try again later.'));

    emitOpenSupportWidgetEvent({ user });
  }, []);

  const showSpinner = useMemo(() => {
    return helpTopicsNotAvailable || helpArticlesNotAvailable || requestState === LoadingConstants.LOADING;
  }, [helpTopicsNotAvailable, helpArticlesNotAvailable, requestState]);

  useEffect(() => {
    const fetchGetHelpArticlesArgs: FetchGetHelpArticlesArgs = {
      params: { ...formState, category: formState?.category?.key, topic: formState?.topic?.key },
      onSuccess: setHelpArticles,
      onError: () => setErrorAlert(''),
    };

    if (loggedIn) {
      fetchGetHelpArticlesForLoggedInUsers(fetchGetHelpArticlesArgs);
    } else {
      fetchGetHelpArticles(fetchGetHelpArticlesArgs);
    }

    if (setCategorySelected) setCategorySelected(Boolean(formState.category));
  }, [formState?.category, formState?.topic, loggedIn, setCategorySelected]);

  const onSubmit = () => {
    if (isUsingWidget) {
      emitSubmitWidgetSupportRequestEvent({ user: zrmwUser || user });
    }
  };

  const renderSuccess = () => (
    <Spacer padding="md">
      <Alert appearance="success" body={<Text>Thank you! Your request has been submitted. </Text>} />
    </Spacer>
  );

  const renderForm = () => {
    const formWidth = helpArticlesNotAvailable ? '100%' : '60%';
    return (
      <StyledFlex display="flex" justifyContent="space-between">
        <Spacer marginRight="xs" style={{ minWidth: formWidth }}>
          {!helpTopicsNotAvailable && !helpArticlesNotAvailable && (
            <SubmitRequestForm
              setCanSubmit={setCanSubmit}
              setRequestState={setRequestState}
              setFormState={setFormState}
              setErrorAlert={setErrorAlert}
              onSubmit={onSubmit}
              submitClicked={submitClicked}
              formState={formState}
              helpTopics={helpTopics}
              zrmwUser={zrmwUser}
              queryObject={queryObject}
              zrmSignInPage={zrmSignInPage}
            />
          )}
          {zrmwUser && renderFooter?.()}
        </Spacer>
        {!helpArticlesNotAvailable && <HelpArticlesContent data={helpArticles} error={false} />}
      </StyledFlex>
    );
  };

  const renderState = () => {
    switch (true) {
      case Boolean(requestState === LoadingConstants.ERROR):
        return (
          <ModalWrapper
            heading="Unable to submit request"
            onClose={() => {
              setErrorAlert('');
              setSubmitClicked(false);
              setRequestState(LoadingConstants.INITIAL);
            }}
          >
            <Text>{errorAlert}</Text>
          </ModalWrapper>
        );
      case Boolean(errorAlert):
        return <Alert appearance="error" body={errorAlert} />;
      case Boolean(requestState === LoadingConstants.SUCCESS):
        return renderSuccess();
      case Boolean(showSpinner):
        return <SpinnerContainer size="lg" height="600px" />;
      case Boolean(helpTopics?.categories?.length):
        return renderForm();
      default:
        return null;
    }
  };
  return <Spacer>{renderState()}</Spacer>;
};

export interface SubmitRequestPageProps {
  zrmwUser?: RentalManagerAuthCheckUserInfoDto;
  zrmSignInPage?: string;
}

/* Re-implementation of zrmw/SubmitRequestPage using SubmitRequestPageComponent */
export const SubmitRequestPage = ({ zrmwUser, zrmSignInPage }: SubmitRequestPageProps) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [categorySelected, setCategorySelected] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const queryObject = {
    categoryKey: params.get('category'),
    topicKey: params.get('topic'),
    listingAddress: params.get('listingAddress'),
    unit: params.get('unit'),
    listingAlias: params.get('listingAlias'),
  };

  const afterSubmit = submitClicked && canSubmit;
  const showFooter = categorySelected && !afterSubmit;

  return (
    <>
      <StyledPage data-testid="SubmitRequestPage" backgroundColor="white">
        <PageBreadcrumb
          backButton={
            <TextButton as="a" href="https://help.zillowrentalmanager.com/hc/en-us">
              Help center | Zillow Rental Manager
            </TextButton>
          }
        />
        <PageTitle heading={<Heading level={1}>Submit a request</Heading>} />
        <PageContent>
          <SubmitRequestPageComponent
            setSubmitClicked={setSubmitClicked}
            submitClicked={submitClicked}
            setCanSubmit={setCanSubmit}
            setCategorySelected={setCategorySelected}
            zrmwUser={zrmwUser}
            queryObject={queryObject}
            zrmSignInPage={zrmSignInPage}
            renderFooter={
              showFooter
                ? () => (
                    <StyledSpacer marginY="md">
                      <Flex display="flex" justifyContent={{ default: 'flex-end', lg_gte: 'flex-start' }}>
                        <SubmitRequestModalPageFooter
                          onClick={() => setSubmitClicked(true)}
                          disabled={!canSubmit}
                          customSubmitText={submitClicked ? 'Next' : undefined}
                        />
                      </Flex>
                    </StyledSpacer>
                  )
                : undefined
            }
          />
        </PageContent>
      </StyledPage>
    </>
  );
};
